"use client";

import { cn } from "@/lib/utils";
import { forwardRef } from "react";

export type ThreeStateValue = "off" | "on";

interface StateSwitchProps {
  value: ThreeStateValue;
  onValueChange: (value: ThreeStateValue) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
}

const StateSwitch = forwardRef<HTMLButtonElement, StateSwitchProps>(
  (
    { value, onValueChange, disabled = false, className, id, ...props },
    ref
  ) => {
    const handleClick = () => {
      if (disabled) return;

      // Cycle through the two states: off -> on -> off
      switch (value) {
        case "off":
          onValueChange("on");
          break;
        case "on":
          onValueChange("off");
          break;
        default:
          onValueChange("off");
      }
    };

    const getSwitchStyles = () => {
      switch (value) {
        case "off":
          return {
            background: "bg-gray-500",
            thumb: "translate-x-0",
            thumbColor: "bg-white",
          };
        case "on":
          return {
            background: "bg-primary-500",
            thumb: "translate-x-4",
            thumbColor: "bg-white",
          };
        default:
          return {
            background: "bg-gray-500",
            thumb: "translate-x-0",
            thumbColor: "bg-white",
          };
      }
    };

    const styles = getSwitchStyles();

    return (
      <button
        ref={ref}
        id={id}
        type="button"
        role="switch"
        aria-checked={value === "on" ? "true" : "false"}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "relative inline-flex h-4.5 w-8 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
          styles.background,
          className
        )}
        {...props}
      >
        <span
          className={cn(
            "pointer-events-none inline-block h-3.5 w-3.5 rounded-full shadow-lg ring-0 transition-transform duration-200 ease-in-out",
            styles.thumb,
            styles.thumbColor
          )}
        />
      </button>
    );
  }
);

StateSwitch.displayName = "StateSwitch";

export { StateSwitch };
